import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAsync } from 'react-use';
import { BookApi } from '../../API/BookApi';
import { StorylineApi } from '../../API/StorylineApi';
import { useAuth } from '../../Auth/AuthProvider';
import Button from '../../Components/Button';
import LoadingIcon from '../../Components/LoadingIcon';
import { AnalyticsEvent, useAnalytics } from '../../Context/AnalyticsProvider';
import { useNavigate } from '../../ProtectedRoute';
import { StorylineDilemmaChoiceInput } from '../../Types/StorylineDilemma';
import StorylineDilemmaOptionsSelectView from '../Controls/StorylineDilemmaOptionsSelectView';
import { BuilderGenerationStatus } from '../Types/BuilderGenerationStatus';
import ChapterBuilderInProgressView from './ChapterBuilderInProgressView';

interface LocationState {
  requestId?: string;
  generationStartedAt?: Date;
}

const ChapterBuilderView: React.FC = () => {
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  const { isAuthenticated, signUpAsAnonymous } = useAuth();
  const { bookId, chapterId } = useParams<{
    bookId: string;
    chapterId: string | undefined;
  }>();
  const location = useLocation();
  const { requestId, generationStartedAt } = (location.state ||
    {}) as LocationState;

  const [generationStatus, setGenerationStatus] =
    useState<BuilderGenerationStatus>(
      chapterId
        ? BuilderGenerationStatus.IN_PROGRESS
        : BuilderGenerationStatus.NOT_REQUESTED,
    );
  const [generationStartedAtState, setGenerationStartedAtState] =
    useState<Date | null>(generationStartedAt);
  const [
    selectedDilemmaOptionByDilemmaId,
    setSelectedDilemmaOptionByDilemmaId,
  ] = useState<Record<string, string>>({});
  const [errorMessage, setErrorMessage] = useState<string>();

  const { value: book } = useAsync(async () => {
    const bookApi = new BookApi();
    return await bookApi.getBook(bookId);
  }, [bookId]);

  const lastChapter = useMemo(() => {
    if (!book) return undefined;

    return book.chapters.sort((a, b) => b.chapterNumber - a.chapterNumber)[0];
  }, [book]);

  const { value: dilemmas } = useAsync(async () => {
    if (!bookId || !lastChapter?.id) return undefined;

    const dilemmaApi = new StorylineApi();
    return await dilemmaApi.getChapterDilemmas(bookId, lastChapter.id);
  }, [bookId, lastChapter?.id]);

  const dilemmaChoices = useMemo<StorylineDilemmaChoiceInput[]>(() => {
    return dilemmas
      ?.filter((dilemma) => selectedDilemmaOptionByDilemmaId[dilemma.id])
      .map((dilemma) => ({
        dilemmaId: dilemma.id,
        chosenOptionId: selectedDilemmaOptionByDilemmaId[dilemma.id],
      }));
  }, [dilemmas, selectedDilemmaOptionByDilemmaId]);

  const canSubmit = useMemo(() => {
    if (!dilemmas) return false;
    return dilemmas.every(
      (dilemma) => selectedDilemmaOptionByDilemmaId[dilemma.id],
    );
  }, [dilemmas, selectedDilemmaOptionByDilemmaId]);

  async function onSubmit() {
    try {
      setGenerationStatus(BuilderGenerationStatus.REQUESTED);
      setGenerationStartedAtState(new Date());
      setErrorMessage(undefined);

      await trackEvent(AnalyticsEvent.CHAPTER_BUILDER_GENERATION_REQUESTED, {
        selectedDilemmaOptions: selectedDilemmaOptionByDilemmaId,
      });

      if (!isAuthenticated) {
        await signUpAsAnonymous();
      }

      const bookApi = new BookApi();
      const response = await bookApi.triggerAsyncNextChapterGeneration({
        bookId,
        dilemmaChoices,
      });

      navigate(`/create-my-own-story/${bookId}/${response.newChapterId}`, {
        state: {
          requestId: response.requestId,
          generationStartedAt: generationStartedAtState,
        },
        replace: true,
      });
    } catch (error) {
      console.error('Error generating chapter:', error);

      // Show toast notification
      toast.error('Unable to create your chapter. Please try again.');

      // Reset generation status
      setGenerationStatus(BuilderGenerationStatus.NOT_REQUESTED);

      // Track the error event
      await trackEvent(AnalyticsEvent.CHAPTER_BUILDER_GENERATION_FAILED, {
        error: error?.toString() || 'Unknown Error',
        selectedDilemmaOptions: selectedDilemmaOptionByDilemmaId,
      });
    }
  }

  if (!dilemmas || !book) {
    return <LoadingIcon fullScreen />;
  }

  if (generationStatus === BuilderGenerationStatus.IN_PROGRESS) {
    return (
      <ChapterBuilderInProgressView
        bookId={bookId}
        chapterId={chapterId}
        requestId={requestId}
        generationStartedAt={generationStartedAtState}
        onGenerationError={() => {
          setGenerationStatus(BuilderGenerationStatus.NOT_REQUESTED);
        }}
      />
    );
  }

  const nextChapterNumber = book.chapters.length + 1;

  return (
    <div className="relative w-full min-h-screen">
      <Link
        to={`/book/${bookId}/${lastChapter.id}`}
        className="flex items-center text-sm text-gray-500 hover:text-gray-700 ml-4 mt-4"
      >
        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
        Back to Chapter {book.chapters.length}
      </Link>
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-4xl font-bold text-center mb-16">
          What Happens Next?
        </h1>

        <div className="max-w-5xl mx-auto space-y-8">
          {dilemmas.map((dilemma) => (
            <div key={dilemma.id} className="space-y-4">
              <StorylineDilemmaOptionsSelectView
                dilemma={dilemma}
                selectedOptionIds={[
                  selectedDilemmaOptionByDilemmaId[dilemma.id],
                ]}
                onOptionSelect={(optionId) => {
                  setSelectedDilemmaOptionByDilemmaId((prev) => ({
                    ...prev,
                    [dilemma.id]: optionId,
                  }));
                }}
              />
            </div>
          ))}

          <div className="flex flex-col items-center justify-center pt-8">
            <Button
              onClick={onSubmit}
              disabled={!canSubmit}
              isInProgress={
                generationStatus === BuilderGenerationStatus.REQUESTED
              }
              inProgressText={`Creating Chapter ${nextChapterNumber}...`}
            >
              Create Chapter {nextChapterNumber}
            </Button>
            {errorMessage && (
              <div className="text-red-500 mb-4">{errorMessage}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChapterBuilderView;
